/**
 * Admin Global Settings Page
 *
 * Manage all records in the skillsnap-global-settings DynamoDB table.
 * Each setting is displayed as a collapsible card with a JSON editor.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import {
  Save, Loader2, AlertCircle, CheckCircle, Plus, Trash2,
  ChevronDown, ChevronRight, X, RefreshCw, Copy, Check,
  ToggleLeft, ToggleRight,
} from 'lucide-react';

interface GlobalSetting {
  setting_key: string;
  setting_value: any;
  updated_at?: string;
  updated_by?: string;
}

/** Feature flags stored under `feature_flags` key in global-settings.
 *  Each flag is a simple boolean on/off toggle. */
interface FeatureFlags {
  [key: string]: boolean;
}

const FEATURE_FLAG_KEY = 'feature_flags';

/** Registry of known feature flags with display labels and descriptions */
const FEATURE_FLAG_REGISTRY: { key: string; label: string; description: string }[] = [
  {
    key: 'section_generation_ai_validation',
    label: 'Section Generation AI Validation & Fix',
    description:
      'When ON, every generated section is validated and auto-fixed by a secondary AI call. ' +
      'Turn OFF during development to surface raw generation errors immediately.',
  },
  {
    key: 'enable_freq_tags',
    label: 'Job Tags: Frequency-Based Deterministic Tags',
    description:
      'When ON, adds deterministic tags extracted by word frequency (3+ occurrences) from the job description, ' +
      'as a safety net for terms the LLM might miss. When OFF, job tags come only from the LLM + defaults.',
  },
];

export function GlobalSettings() {
  const [settings, setSettings] = useState<GlobalSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [editBuffers, setEditBuffers] = useState<Record<string, string>>({});
  const [parseErrors, setParseErrors] = useState<Record<string, string>>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [newSettingKey, setNewSettingKey] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({});
  const [savingFlags, setSavingFlags] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getAdminGlobalSettings();
      setSettings(data.settings || []);
      // Initialize edit buffers
      const buffers: Record<string, string> = {};
      for (const s of data.settings || []) {
        buffers[s.setting_key] = JSON.stringify(s.setting_value, null, 2);
      }
      setEditBuffers(buffers);
      setParseErrors({});
      // Extract feature flags
      const ffSetting = (data.settings || []).find((s: GlobalSetting) => s.setting_key === FEATURE_FLAG_KEY);
      if (ffSetting && typeof ffSetting.setting_value === 'object') {
        setFeatureFlags(ffSetting.setting_value);
      } else {
        setFeatureFlags({});
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load global settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  function toggleExpanded(key: string) {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleBufferChange(key: string, value: string) {
    setEditBuffers(prev => ({ ...prev, [key]: value }));
    // Validate JSON
    try {
      JSON.parse(value);
      setParseErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (e: any) {
      setParseErrors(prev => ({ ...prev, [key]: e.message }));
    }
  }

  function formatJson(key: string) {
    const raw = editBuffers[key];
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      setEditBuffers(prev => ({ ...prev, [key]: JSON.stringify(parsed, null, 2) }));
      setParseErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch { /* leave as-is if invalid */ }
  }

  async function handleSave(key: string) {
    const raw = editBuffers[key];
    if (!raw) return;
    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch (e: any) {
      setParseErrors(prev => ({ ...prev, [key]: e.message }));
      return;
    }

    setSavingKeys(prev => new Set(prev).add(key));
    setError(null);
    setSuccess(null);
    try {
      await api.updateAdminGlobalSetting(key, parsed);
      setSuccess(`Setting "${key}" saved successfully`);
      setTimeout(() => setSuccess(null), 3000);
      await loadSettings();
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to save "${key}"`);
    } finally {
      setSavingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  async function handleDelete(key: string) {
    if (!confirm(`Delete setting "${key}"? This cannot be undone.`)) return;
    setDeletingKey(key);
    setError(null);
    setSuccess(null);
    try {
      await api.deleteAdminGlobalSetting(key);
      setSuccess(`Setting "${key}" deleted`);
      setTimeout(() => setSuccess(null), 3000);
      await loadSettings();
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to delete "${key}"`);
    } finally {
      setDeletingKey(null);
    }
  }

  async function handleCreate() {
    const key = newSettingKey.trim();
    if (!key) return;
    if (settings.some(s => s.setting_key === key)) {
      setError(`Setting "${key}" already exists`);
      return;
    }
    setSavingKeys(prev => new Set(prev).add(key));
    setError(null);
    try {
      await api.updateAdminGlobalSetting(key, {});
      setSuccess(`Setting "${key}" created`);
      setTimeout(() => setSuccess(null), 3000);
      setNewSettingKey('');
      setShowNewForm(false);
      await loadSettings();
      setExpandedKeys(prev => new Set(prev).add(key));
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to create "${key}"`);
    } finally {
      setSavingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  async function toggleFeatureFlag(flagKey: string) {
    const updated = { ...featureFlags, [flagKey]: !featureFlags[flagKey] };
    setSavingFlags(true);
    setError(null);
    setSuccess(null);
    try {
      await api.updateAdminGlobalSetting(FEATURE_FLAG_KEY, updated);
      setFeatureFlags(updated);
      setSuccess(`Feature flag "${flagKey}" ${updated[flagKey] ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(null), 3000);
      // Refresh the settings list so the JSON editor stays in sync
      await loadSettings();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update feature flag');
    } finally {
      setSavingFlags(false);
    }
  }

  function copyToClipboard(key: string) {
    const raw = editBuffers[key];
    if (!raw) return;
    navigator.clipboard.writeText(raw);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  }

  function hasUnsavedChanges(key: string): boolean {
    const setting = settings.find(s => s.setting_key === key);
    if (!setting) return false;
    const original = JSON.stringify(setting.setting_value, null, 2);
    return editBuffers[key] !== original;
  }

  function getValuePreview(value: any): string {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'string') return `"${value.length > 60 ? value.slice(0, 60) + '...' : value}"`;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return `Array [${value.length} items]`;
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      return `Object {${keys.slice(0, 3).join(', ')}${keys.length > 3 ? ', ...' : ''}}`;
    }
    return String(value);
  }

  // Estimate textarea height from content
  function getTextareaRows(text: string): number {
    const lines = text.split('\n').length;
    return Math.max(6, Math.min(30, lines + 1));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Global Settings</h1>
            <p className="text-sm text-gray-500 mt-1">
              {settings.length} settings in <code className="bg-gray-100 px-1 rounded text-xs">skillsnap-global-settings</code>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadSettings}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => setShowNewForm(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 font-medium"
            >
              <Plus className="w-4 h-4" />
              New Setting
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="flex items-center p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="flex items-center p-3 mb-4 bg-green-50 text-green-700 rounded-lg text-sm">
            <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {success}
          </div>
        )}

        {/* New Setting Form */}
        {showNewForm && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={newSettingKey}
                onChange={e => setNewSettingKey(e.target.value.replace(/[^a-z0-9_]/g, ''))}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                placeholder="setting_key (lowercase, underscores)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                autoFocus
              />
              <button
                onClick={handleCreate}
                disabled={!newSettingKey.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium"
              >
                Create
              </button>
              <button
                onClick={() => { setShowNewForm(false); setNewSettingKey(''); }}
                className="px-3 py-2 text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Feature Flags */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Feature Flags</h2>
          <p className="text-xs text-gray-500 mb-3">
            Simple on/off toggles stored in <code className="bg-gray-100 px-1 rounded">{FEATURE_FLAG_KEY}</code>
          </p>
          <div className="space-y-2">
            {FEATURE_FLAG_REGISTRY.map(flag => {
              const isOn = !!featureFlags[flag.key];
              return (
                <div
                  key={flag.key}
                  className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm"
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{flag.label}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        isOn ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {isOn ? 'On' : 'Off'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{flag.description}</p>
                  </div>
                  <button
                    onClick={() => toggleFeatureFlag(flag.key)}
                    disabled={savingFlags}
                    className="flex-shrink-0 focus:outline-none disabled:opacity-50"
                    title={isOn ? 'Turn off' : 'Turn on'}
                  >
                    {isOn
                      ? <ToggleRight className="w-10 h-10 text-green-600" />
                      : <ToggleLeft className="w-10 h-10 text-gray-400" />
                    }
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Settings Cards */}
        <div className="space-y-3">
          {settings.map(setting => {
            const key = setting.setting_key;
            const expanded = expandedKeys.has(key);
            const saving = savingKeys.has(key);
            const deleting = deletingKey === key;
            const hasError = !!parseErrors[key];
            const unsaved = hasUnsavedChanges(key);

            return (
              <div key={key} className={`bg-white rounded-lg border ${unsaved ? 'border-amber-300 shadow-amber-100' : 'border-gray-200'} shadow-sm`}>
                {/* Card Header */}
                <div
                  className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 rounded-t-lg"
                  onClick={() => toggleExpanded(key)}
                >
                  {expanded
                    ? <ChevronDown className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                  }
                  <code className="font-mono text-sm font-semibold text-gray-900 mr-3">{key}</code>
                  {unsaved && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full mr-2">unsaved</span>
                  )}
                  {!expanded && (
                    <span className="text-xs text-gray-400 truncate flex-1">{getValuePreview(setting.setting_value)}</span>
                  )}
                  {setting.updated_at && (
                    <span className="text-xs text-gray-400 ml-auto pl-2 flex-shrink-0">
                      {new Date(setting.updated_at).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Expanded Editor */}
                {expanded && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    {/* Meta info */}
                    {setting.updated_at && (
                      <p className="text-xs text-gray-400 mt-2 mb-2">
                        Updated: {new Date(setting.updated_at).toLocaleString()}
                        {setting.updated_by && <> by <span className="font-mono">{setting.updated_by}</span></>}
                      </p>
                    )}

                    {/* JSON Editor */}
                    <textarea
                      value={editBuffers[key] || ''}
                      onChange={e => handleBufferChange(key, e.target.value)}
                      onBlur={() => formatJson(key)}
                      rows={getTextareaRows(editBuffers[key] || '')}
                      className={`w-full font-mono text-sm p-3 border rounded-lg resize-y ${
                        hasError ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-gray-50'
                      } focus:outline-none focus:ring-2 ${hasError ? 'focus:ring-red-300' : 'focus:ring-primary-300'}`}
                      spellCheck={false}
                    />

                    {/* Parse Error */}
                    {hasError && (
                      <p className="text-xs text-red-600 mt-1 font-mono">{parseErrors[key]}</p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSave(key)}
                          disabled={saving || hasError}
                          className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5 text-sm font-medium"
                        >
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => copyToClipboard(key)}
                          className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 flex items-center gap-1.5 text-sm"
                        >
                          {copiedKey === key ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedKey === key ? 'Copied' : 'Copy'}
                        </button>
                        {unsaved && (
                          <button
                            onClick={() => {
                              const original = JSON.stringify(setting.setting_value, null, 2);
                              setEditBuffers(prev => ({ ...prev, [key]: original }));
                              setParseErrors(prev => { const next = { ...prev }; delete next[key]; return next; });
                            }}
                            className="px-3 py-1.5 text-gray-500 hover:text-gray-700 text-sm"
                          >
                            Discard
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => handleDelete(key)}
                        disabled={deleting}
                        className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1.5 text-sm"
                      >
                        {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {settings.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500">
            No global settings found. Create one to get started.
          </div>
        )}
      </div>
    </div>
  );
}
